/**
 * Agrège orgStatsDaily[] sur une période → AnalyticsData.
 * Zéro scan orders : tout vient des stats pré-calculées.
 */

import type { OrgStatsDailyDoc } from '@/services/firebase/org-stats-repository';
import type {
    AnalyticsData,
    BusinessKPIs,
    PaymentKPIs,
    DeliveryKPIs,
    CustomerKPIs,
    LiveKPIs,
    AdvancedKPIs,
    TimingKPIs,
    TimeSlotData,
    BestTimeSlot,
} from './types';

const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function mergeMaps(maps: Record<string, number>[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const m of maps) {
        for (const [k, v] of Object.entries(m)) {
            out[k] = (out[k] ?? 0) + v;
        }
    }
    return out;
}

function dateToDayOfWeek(dateStr: string): number {
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    return day === 0 ? 6 : day - 1;
}

/** YYYY-MM-DD en heure locale (aligné avec orgStatsDaily.day et toYYYYMMDD du dashboard). */
function toYYYYMMDDLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function aggregateOrgStatsToAnalytics(
    docs: OrgStatsDailyDoc[],
    startDay: string,
    endDay: string
): AnalyticsData {
    if (docs.length === 0) {
        return getEmptyAnalyticsData();
    }

    const business = aggregateBusiness(docs, startDay, endDay);
    const payment = aggregatePayment(docs);
    const delivery = aggregateDelivery(docs);
    const customer = aggregateCustomer(docs, startDay, endDay);
    const live = aggregateLive(docs);
    const advanced = aggregateAdvanced(docs);
    const timing = aggregateTiming(docs);

    return { business, payment, delivery, customer, live, advanced, timing };
}

function getEmptyAnalyticsData(): AnalyticsData {
    return {
        business: {
            totalRevenue: 0,
            salesCount: 0,
            averageCart: 0,
            netRevenue: 0,
            productsSold: 0,
            revenueByDay: [],
            salesByDay: [],
            salesByCountry: [],
            salesByCity: [],
        },
        payment: {
            successRate: 0,
            averagePaymentDelay: 0,
            medianPaymentDelay: 0,
            latePaymentsPercent: 0,
            totalPaidOrders: 0,
            dominantMethod: 'stripe',
            pendingPayments: 0,
            statusDistribution: [],
            paymentDelays: [],
            methodDistribution: [],
        },
        delivery: {
            averageDeliveryCost: 0,
            mainCarrier: 'N/A',
            homeVsPickup: { home: 0, pickup: 0 },
            ordersWithTracking: 0,
            ordersWithoutTracking: 0,
            estimatedDeliveryTime: 0,
            carrierDistribution: [],
            deliveryCostRanges: [],
        },
        customer: {
            uniqueCustomers: 0,
            customersByLevel: { occasionnel: 0, regulier: 0, fidele: 0, vip: 0 },
            engagedCustomersPercent: 0,
            loyalRevenuePercent: 0,
            averageCart: 0,
            topCustomers: [],
            levelDistribution: [],
            levelRevenueDistribution: [],
            levelByPeriod: [],
            totalOrders: 0,
        },
        live: {
            peakHour: '00h-01h',
            peakSalesCount: 0,
            multiPurchaseCustomers: 0,
            salesByHour: Array.from({ length: 24 }, (_, h) => ({
                hour: `${String(h).padStart(2, '0')}h`,
                count: 0,
            })),
        },
        advanced: {
            mostProfitableProduct: { reference: 'N/A', revenue: 0 },
            avgOrderToPaymentDelay: 0,
            avgOrderToShippingDelay: 0,
            sellerScore: 0,
        },
        timing: {
            heatmapData: Array(7).fill(null).map((_, day) =>
                Array(24).fill(null).map((_, hour) => ({
                    day,
                    hour,
                    orders: 0,
                    revenue: 0,
                    avgCart: 0,
                    uniqueCustomers: 0,
                }))
            ),
            topSlots: [],
            bestSlot: null,
            totalOrdersAnalyzed: 0,
            avgOrdersPerSlot: 0,
            avgRevenuePerSlot: 0,
        },
    };
}

function fillDailyRange(startDay: string, endDay: string): string[] {
    const out: string[] = [];
    const start = new Date(startDay + 'T12:00:00');
    const end = new Date(endDay + 'T12:00:00');
    const cur = new Date(start);
    while (cur <= end) {
        out.push(toYYYYMMDDLocal(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

function aggregateBusiness(
    docs: OrgStatsDailyDoc[],
    startDay: string,
    endDay: string
): BusinessKPIs {
    const totalRevenue = docs.reduce((s, d) => s + d.revenueGrossCents, 0) / 100;
    const salesCount = docs.reduce((s, d) => s + d.ordersPaidCount, 0);
    const netRevenue = docs.reduce((s, d) => s + d.revenueNetCents, 0) / 100;
    const productsSold = docs.reduce((s, d) => s + d.itemsPaidCount, 0);

    // AOV (Panier moyen) = Revenue / Nombre de commandes uniques (ordersPaidCount est maintenant corrigé)
    const averageCart = salesCount > 0 ? totalRevenue / salesCount : 0;

    // Prix moyen par article = Revenue / Nombre d'articles vendus
    const averageItemValue = productsSold > 0 ? totalRevenue / productsSold : 0;

    const byDay = new Map<string, { revenue: number; count: number }>();
    for (const d of docs) {
        byDay.set(d.day, {
            revenue: d.revenueGrossCents / 100,
            count: d.ordersPaidCount,
        });
    }
    const days = fillDailyRange(startDay, endDay);
    const revenueByDay = days.map((date) => ({ date, amount: byDay.get(date)?.revenue ?? 0 }));
    const salesByDay = days.map((date) => ({ date, count: byDay.get(date)?.count ?? 0 }));

    const countryCount = mergeMaps(docs.map((d) => d.countryCount));
    const countryRevenueCents = mergeMaps(docs.map((d) => d.countryRevenueCents));
    const salesByCountry = Object.keys(countryCount).map((country) => ({
        country,
        count: countryCount[country],
        revenue: (countryRevenueCents[country] ?? 0) / 100,
    })).sort((a, b) => b.revenue - a.revenue);

    const cityCount = mergeMaps(docs.map((d) => d.cityCount ?? {}));
    const cityRevenueCents = mergeMaps(docs.map((d) => d.cityRevenueCents ?? {}));
    const salesByCity = Object.keys(cityCount).map((cityKey) => {
        const lastPipe = cityKey.lastIndexOf('|');
        const city = lastPipe >= 0 ? cityKey.slice(0, lastPipe) : cityKey;
        const country = lastPipe >= 0 ? cityKey.slice(lastPipe + 1) : '';
        return {
            city,
            country,
            count: cityCount[cityKey],
            revenue: (cityRevenueCents[cityKey] ?? 0) / 100,
        };
    }).sort((a, b) => b.revenue - a.revenue);

    return {
        totalRevenue,
        salesCount,
        averageCart,
        averageItemValue,
        netRevenue,
        productsSold,
        revenueByDay,
        salesByDay,
        salesByCountry,
        salesByCity,
    };
}

function aggregatePayment(docs: OrgStatsDailyDoc[]): PaymentKPIs {
    const succeeded = docs.reduce((s, d) => s + d.paymentsSucceeded, 0);
    const failed = docs.reduce((s, d) => s + d.paymentsFailed, 0);
    const totalAttempts = succeeded + failed;
    const successRate = totalAttempts > 0 ? (succeeded / totalAttempts) * 100 : 0;

    const delaySum = docs.reduce((s, d) => s + d.paymentDelaySumSeconds, 0);
    const delayCount = docs.reduce((s, d) => s + d.paymentDelayCount, 0);
    const avgDelayMinutes = delayCount > 0 ? (delaySum / 60) / delayCount : 0;
    const lateCount = docs.reduce((s, d) => s + d.latePaymentsCount, 0);
    const latePaymentsPercent = delayCount > 0 ? (lateCount / delayCount) * 100 : 0;

    const buckets = mergeMaps(docs.map((d) => d.delayBuckets));
    const paymentDelays = Object.entries(buckets).map(([range, count]) => ({ range, count }));

    const methodCount = mergeMaps(docs.map((d) => d.paymentMethodCount));
    const totalMethods = Object.values(methodCount).reduce((a, b) => a + b, 0);
    const methodLabel = (m: string) => (m === 'stripe' ? 'Stripe' : m === 'paypal' ? 'PayPal' : m);
    const methodDistribution = Object.entries(methodCount).map(([method, count]) => ({
        method: methodLabel(method),
        count,
        percentage: totalMethods > 0 ? (count / totalMethods) * 100 : 0,
    })).filter((m) => m.count > 0);

    const stripe = methodCount['stripe'] ?? methodCount['stripe_api_key'] ?? 0;
    const paypal = methodCount['paypal'] ?? 0;
    const dominantMethod = stripe >= paypal ? 'stripe' : 'paypal';

    const statusDistribution = [
        { status: 'Payé', count: succeeded, percentage: totalAttempts > 0 ? (succeeded / totalAttempts) * 100 : 0 },
        { status: 'En attente', count: 0, percentage: 0 },
        ...(failed > 0 ? [{ status: 'Échoué', count: failed, percentage: (failed / totalAttempts) * 100 }] : []),
    ].filter((s) => s.count > 0) as { status: string; count: number; percentage: number }[];

    return {
        successRate,
        averagePaymentDelay: avgDelayMinutes,
        medianPaymentDelay: avgDelayMinutes,
        latePaymentsPercent,
        totalPaidOrders: delayCount,
        dominantMethod,
        pendingPayments: 0,
        statusDistribution,
        paymentDelays,
        methodDistribution,
    };
}

function aggregateDelivery(docs: OrgStatsDailyDoc[]): DeliveryKPIs {
    const shippingOrders = docs.reduce((s, d) => s + d.shippingOrdersCount, 0);
    const shippingRevenueCents = docs.reduce((s, d) => s + d.shippingRevenueCents, 0);
    const averageDeliveryCost = shippingOrders > 0 ? (shippingRevenueCents / 100) / shippingOrders : 0;

    const carrierCount = mergeMaps(docs.map((d) => d.carrierCount));
    let mainCarrier = 'N/A';
    let maxCount = 0;
    for (const [c, n] of Object.entries(carrierCount)) {
        if (n > maxCount) {
            maxCount = n;
            mainCarrier = c;
        }
    }

    const home = docs.reduce((s, d) => s + d.homeDeliveryCount, 0);
    const pickup = docs.reduce((s, d) => s + d.relayDeliveryCount, 0);
    const withTracking = docs.reduce((s, d) => s + d.ordersWithTrackingCount, 0);
    const total = shippingOrders || 1;
    const carrierDistribution = Object.entries(carrierCount).map(([carrier, count]) => ({
        carrier,
        count,
        percentage: (count / total) * 100,
    }));

    const costRanges: Record<string, number> = { '0-5€': 0, '5-10€': 0, '10-15€': 0, '15€+': 0 };
    if (shippingOrders > 0) {
        const avg = (shippingRevenueCents / 100) / shippingOrders;
        if (avg <= 5) costRanges['0-5€'] = shippingOrders;
        else if (avg <= 10) costRanges['5-10€'] = shippingOrders;
        else if (avg <= 15) costRanges['10-15€'] = shippingOrders;
        else costRanges['15€+'] = shippingOrders;
    }
    const deliveryCostRanges = Object.entries(costRanges).map(([range, count]) => ({ range, count }));

    return {
        averageDeliveryCost,
        mainCarrier,
        homeVsPickup: { home, pickup },
        ordersWithTracking: withTracking,
        ordersWithoutTracking: Math.max(0, shippingOrders - withTracking),
        estimatedDeliveryTime: 0,
        carrierDistribution,
        deliveryCostRanges,
    };
}

function aggregateCustomer(docs: OrgStatsDailyDoc[], startDay: string, endDay: string): CustomerKPIs {
    const totalRevenue = docs.reduce((s, d) => s + d.revenueGrossCents, 0) / 100;
    const totalOrders = docs.reduce((s, d) => s + d.ordersPaidCount, 0);
    /** Somme des clients actifs par jour (un client peut compter plusieurs fois sur la période). Cohérent avec new/returning en CA. */
    const uniqueCustomers = docs.reduce((s, d) => s + d.activeCustomersCount, 0);
    const newCount = docs.reduce((s, d) => s + d.newCustomersCount, 0);
    const returningCount = docs.reduce((s, d) => s + d.returningCustomersCount, 0);
    const newRevenue = docs.reduce((s, d) => s + d.revenueNewCustomersCents, 0) / 100;
    const existingRevenue = docs.reduce((s, d) => s + d.revenueReturningCustomersCents, 0) / 100;
    const averageCart = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const total = uniqueCustomers || 1;
    const levelDistribution = [
        { level: 'Nouveau', count: newCount, percentage: (newCount / total) * 100 },
        { level: 'Existant', count: returningCount, percentage: (returningCount / total) * 100 },
    ];
    const levelRevenueDistribution = [
        { level: 'Nouveau', revenue: newRevenue, percentage: totalRevenue > 0 ? (newRevenue / totalRevenue) * 100 : 0 },
        { level: 'Existant', revenue: existingRevenue, percentage: totalRevenue > 0 ? (existingRevenue / totalRevenue) * 100 : 0 },
    ];
    const byDay = new Map<string, { newC: number; retC: number; newR: number; retR: number }>();
    for (const d of docs) {
        byDay.set(d.day, {
            newC: d.newCustomersCount,
            retC: d.returningCustomersCount,
            newR: d.revenueNewCustomersCents / 100,
            retR: d.revenueReturningCustomersCents / 100,
        });
    }
    const allDays = fillDailyRange(startDay, endDay);
    const levelByPeriod = allDays.map((date) => {
        const x = byDay.get(date);
        return {
            date,
            occasionnel: x?.newC ?? 0,
            regulier: x?.retC ?? 0,
            fidele: 0,
            vip: 0,
        };
    });
    const customerTimeSeries = allDays.map((date) => {
        const x = byDay.get(date);
        return {
            date,
            newCustomers: x?.newC ?? 0,
            existingCustomers: x?.retC ?? 0,
            newRevenue: x?.newR ?? 0,
            existingRevenue: x?.retR ?? 0,
        };
    });

    return {
        uniqueCustomers,
        customersByLevel: { occasionnel: newCount, regulier: returningCount, fidele: 0, vip: 0 },
        engagedCustomersPercent: total > 0 ? (returningCount / total) * 100 : 0,
        loyalRevenuePercent: totalRevenue > 0 ? (existingRevenue / totalRevenue) * 100 : 0,
        averageCart,
        topCustomers: [],
        levelDistribution,
        levelRevenueDistribution,
        levelByPeriod,
        customerTimeSeries,
        totalRevenue,
        totalOrders,
    };
}

function aggregateLive(docs: OrgStatsDailyDoc[]): LiveKPIs {
    const ordersByHour = mergeMaps(docs.map((d) => d.ordersByHour));
    const salesByHour = Array.from({ length: 24 }, (_, h) => {
        const k = String(h);
        const count = ordersByHour[k] ?? 0;
        return { hour: `${k.padStart(2, '0')}h`, count };
    });
    let peakHour = 0;
    let peakSalesCount = 0;
    salesByHour.forEach(({ count }, idx) => {
        if (count > peakSalesCount) {
            peakSalesCount = count;
            peakHour = idx;
        }
    });

    return {
        peakHour: `${String(peakHour).padStart(2, '0')}h-${String(peakHour + 1).padStart(2, '0')}h`,
        peakSalesCount,
        multiPurchaseCustomers: 0,
        salesByHour,
    };
}

function aggregateAdvanced(docs: OrgStatsDailyDoc[]): AdvancedKPIs {
    const delaySum = docs.reduce((s, d) => s + d.paymentDelaySumSeconds, 0);
    const delayCount = docs.reduce((s, d) => s + d.paymentDelayCount, 0);
    const avgOrderToPaymentDelay = delayCount > 0 ? (delaySum / 60) / delayCount : 0;
    const succeeded = docs.reduce((s, d) => s + d.paymentsSucceeded, 0);
    const total = docs.reduce((s, d) => s + d.ordersPaidCount, 0);
    const paymentScore = total > 0 ? Math.min(100, (succeeded / total) * 100) : 0;
    const withTracking = docs.reduce((s, d) => s + d.ordersWithTrackingCount, 0);
    const shippingOrders = docs.reduce((s, d) => s + d.shippingOrdersCount, 0);
    const trackingScore = shippingOrders > 0 ? Math.min(100, (withTracking / shippingOrders) * 100) : 0;
    const sellerScore = Math.round(paymentScore * 0.4 + trackingScore * 0.6);

    return {
        mostProfitableProduct: { reference: 'N/A', revenue: 0 },
        avgOrderToPaymentDelay: avgOrderToPaymentDelay,
        avgOrderToShippingDelay: 0,
        sellerScore,
    };
}

function aggregateTiming(docs: OrgStatsDailyDoc[]): TimingKPIs {
    const heatmap: TimeSlotData[][] = Array(7).fill(null).map((_, day) =>
        Array(24).fill(null).map((_, hour) => ({
            day,
            hour,
            orders: 0,
            revenue: 0,
            avgCart: 0,
            uniqueCustomers: 0,
        }))
    );

    const slotAgg: Record<string, { orders: number; revenue: number }> = {};

    for (const d of docs) {
        const dow = dateToDayOfWeek(d.day);
        for (const [hk, orders] of Object.entries(d.ordersByHour)) {
            const h = Number.parseInt(hk, 10);
            if (h >= 0 && h < 24) {
                heatmap[dow][h].orders += orders;
                const rev = (d.revenueByHourCents[h] ?? 0) / 100;
                heatmap[dow][h].revenue += rev;
                const key = `${dow}_${h}`;
                if (!slotAgg[key]) slotAgg[key] = { orders: 0, revenue: 0 };
                slotAgg[key].orders += orders;
                slotAgg[key].revenue += rev;
            }
        }
    }

    for (let day = 0; day < 7; day++) {
        for (let hour = 0; hour < 24; hour++) {
            const s = heatmap[day][hour];
            s.avgCart = s.orders > 0 ? s.revenue / s.orders : 0;
        }
    }

    const allSlots: { day: number; hour: number; orders: number; revenue: number }[] = [];
    for (const [key, v] of Object.entries(slotAgg)) {
        const [d, h] = key.split('_').map(Number);
        allSlots.push({ day: d, hour: h, orders: v.orders, revenue: v.revenue });
    }
    allSlots.sort((a, b) => b.orders - a.orders);
    const topSlots: BestTimeSlot[] = allSlots.slice(0, 5).map((slot) => ({
        dayLabel: DAY_LABELS[slot.day],
        hourLabel: `${String(slot.hour).padStart(2, '0')}h-${String(slot.hour + 1).padStart(2, '0')}h`,
        orders: slot.orders,
        revenue: slot.revenue,
        isReliable: slot.orders >= 3,
    }));
    const bestSlot = topSlots.length > 0 ? topSlots[0] : null;

    const totalOrders = docs.reduce((s, d) => s + d.ordersPaidCount, 0);
    const slotsWithOrders = allSlots.length || 1;
    const totalRev = allSlots.reduce((a, x) => a + x.revenue, 0);
    const avgOrdersPerSlot = allSlots.reduce((a, x) => a + x.orders, 0) / slotsWithOrders;
    const avgRevenuePerSlot = totalRev / slotsWithOrders;

    return {
        heatmapData: heatmap,
        topSlots,
        bestSlot,
        totalOrdersAnalyzed: totalOrders,
        avgOrdersPerSlot,
        avgRevenuePerSlot,
    };
}
